import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";

import { endpoints } from "@/lib/api";
import { useSettings } from "@/lib/useSettings";
import Icon from "@/components/Icon";
import { Reveal } from "@/lib/motionSafe";
import QuoteForm from "@/components/QuoteForm";

export default function ServiceDetail() {
  const { slug } = useParams();
  const [service, setService] = useState(null);
  const [services, setServices] = useState([]);
  const [notFound, setNotFound] = useState(false);
  const { settings } = useSettings();

  useEffect(() => {
    setService(null);
    setNotFound(false);
    endpoints
      .service(slug)
      .then(setService)
      .catch(() => setNotFound(true));
    endpoints.services().then(setServices).catch(() => {});
  }, [slug]);

  if (notFound) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center px-6" data-testid="service-not-found">
        <div className="text-center">
          <h1 className="font-heading text-4xl font-bold text-[#0A1628]">الخدمة غير موجودة</h1>
          <Link to="/" className="mt-6 inline-block aman-btn-light">العودة إلى الرئيسية</Link>
        </div>
      </div>
    );
  }

  if (!service) {
    return <div className="min-h-[60vh]" />;
  }

  return (
    <div data-testid={`page-service-${service.slug}`}>
      {/* Hero */}
      <section className="aman-hero relative pt-16 pb-24 md:pt-24 md:pb-32 overflow-hidden">
        <div className="aman-glow-orb -top-24 -end-24" aria-hidden="true" />
        <div className="max-w-7xl mx-auto px-6 md:px-10 relative grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7 text-white">
            <Reveal>
              <Link to="/#services" className="inline-flex items-center gap-1 text-cyan-300 hover:text-white text-sm">
                <Icon name="chevron-left" size={16} />
                جميع الخدمات
              </Link>
            </Reveal>
            <Reveal delay={100}>
              <div className="mt-4 flex items-center gap-3">
                <div className="w-14 h-14 rounded-2xl bg-[#00E5FF]/10 border border-[#00E5FF]/40 text-[#00E5FF] flex items-center justify-center">
                  <Icon name={service.icon} size={26} />
                </div>
                <span className="aman-tag">خدمة</span>
              </div>
            </Reveal>
            <Reveal delay={200}>
              <h1 className="font-heading mt-5 text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.15]" data-testid="service-title">
                {service.title_ar}
              </h1>
            </Reveal>
            <Reveal delay={280}>
              <p className="mt-5 text-lg text-slate-300 max-w-2xl leading-relaxed">
                {service.short_desc_ar}
              </p>
            </Reveal>
            <Reveal delay={380}>
              <div className="mt-8 flex flex-wrap gap-3">
                <a href="#service-quote" className="aman-btn-primary text-base" data-testid="service-cta-quote">
                  <Icon name="send" size={18} strokeWidth={2} />
                  اطلب هذه الخدمة
                </a>
                {settings?.phone && (
                  <a href={`tel:${settings.phone}`} className="aman-btn-secondary text-base">
                    <Icon name="phone" size={18} />
                    اتصل بنا
                  </a>
                )}
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-5">
            <Reveal delay={200}>
              <div className="relative rounded-3xl overflow-hidden border border-white/10 shadow-[0_30px_80px_-30px_rgba(0,229,255,0.35)]">
                <img src={service.hero_image} alt={service.title_ar} className="w-full h-[420px] object-cover" />
                <div className="absolute inset-0 bg-gradient-to-tr from-[#0A1628]/60 to-transparent" />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-20 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-3 gap-12">
          <div className="lg:col-span-2">
            <Reveal>
              <p className="text-lg text-slate-700 leading-loose">{service.long_desc_ar}</p>
            </Reveal>
            {service.sections?.map((sec, i) => (
              <Reveal key={i} delay={80 * (i + 1)}>
                <div className="mt-10">
                  <h2 className="font-heading text-2xl sm:text-3xl font-bold text-[#0A1628]">{sec.title_ar}</h2>
                  <p className="mt-3 text-slate-600 leading-loose">{sec.body_ar}</p>
                </div>
              </Reveal>
            ))}
          </div>

          <div>
            <Reveal>
              <div className="aman-card p-6 sticky top-24">
                <h3 className="font-heading text-lg font-bold text-[#0A1628]">ما نقدمه في هذه الخدمة</h3>
                <ul className="mt-4 space-y-3" data-testid="service-features">
                  {service.features?.map((f, i) => (
                    <li key={i} className="flex items-start gap-3 text-slate-700 text-sm">
                      <Icon name="check-circle" size={18} className="text-[#00b8cc] mt-0.5 shrink-0" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Quote form */}
      <section id="service-quote" className="pb-24 bg-white">
        <div className="max-w-3xl mx-auto px-6 md:px-10">
          <Reveal>
            <div className="rounded-3xl bg-slate-50 border border-slate-100 p-8 sm:p-10">
              <h2 className="font-heading text-2xl sm:text-3xl font-bold text-[#0A1628]">اطلب عرض سعر لخدمة {service.title_ar}</h2>
              <p className="text-slate-600 mt-2 text-sm">سيقوم فريقنا بالتواصل معك خلال 24 ساعة.</p>
              <div className="mt-6">
                <QuoteForm
                  services={services}
                  defaultServiceSlug={service.slug}
                  testIdPrefix="quote-form-service"
                />
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
